import { useState, useEffect } from 'react';
import { Loader2, Package, Plus, Play, Lock, ChevronLeft, ChevronRight, X, MessageSquarePlus, Heart, BellRing, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders, getSessionToken } from '../../services/http';
import { tData } from '@/i18n/tData';
import i18n from '@/i18n';
import { langKey } from '@/i18n/fmt';
import { DELIVERY_TYPES } from '../catalog-manager/SpecializedTabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FavoriteButton } from '../FavoriteButton';
import { ProductVideoModal } from './ProductVideoModal';
import { ProductReviewsModal, Stars } from './ProductReviewsModal';
import { ProductShareButtons } from './ProductShareButtons';
import { useCatalogPromos, bestPromos, PromoPriceBlock } from './ProductPromoBadges';
import { SaleModelBadge } from './SaleModelBadge';
import { OscopBuyDialog } from './OscopBuyDialog';
import { ProductSheetModal } from './ProductSheetModal';

const ProductImageCarousel = ({ product, onZoom }) => {
  const [idx, setIdx] = useState(0);
  const imgs = (product.images && product.images.length > 0)
    ? product.images
    : (product.image_url ? [product.image_url] : []);
  if (imgs.length === 0) return <Package className="w-12 h-12 text-white/20" />;
  const go = (e, delta) => {
    e.stopPropagation();
    setIdx((i) => (i + delta + imgs.length) % imgs.length);
  };
  const current = Math.min(idx, imgs.length - 1);
  return (
    <>
      <img src={imgs[current]} alt={product.name}
        onClick={() => onZoom && onZoom(product, current)}
        data-testid={`product-image-${product.sku}`}
        className="w-full h-full object-contain p-1.5 rounded-xl cursor-zoom-in" loading="lazy" />
      {imgs.length > 1 && (
        <>
          <button type="button" onClick={(e) => go(e, -1)}
            data-testid={`carousel-prev-${product.sku}`}
            className="absolute left-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button type="button" onClick={(e) => go(e, 1)}
            data-testid={`carousel-next-${product.sku}`}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid={`carousel-dots-${product.sku}`}>
            {imgs.map((_, i) => (
              <button key={i} type="button"
                onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? 'bg-[#D9B35A]' : 'bg-white/40'}`} />
            ))}
          </div>
        </>
      )}
    </>
  );
};

const ProductLightbox = ({ zoom, onClose }) => {
  const [idx, setIdx] = useState(zoom?.index || 0);
  if (!zoom) return null;
  const { product } = zoom;
  const imgs = (product.images && product.images.length > 0)
    ? product.images
    : (product.image_url ? [product.image_url] : []);
  const current = Math.min(idx, imgs.length - 1);
  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="product-lightbox">
      <button type="button" onClick={onClose} data-testid="lightbox-close"
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
        <X className="w-5 h-5" />
      </button>
      <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          <img src={imgs[current]} alt={product.name}
            className="w-full max-h-[75vh] object-contain rounded-2xl bg-white/[0.03]" />
          {imgs.length > 1 && (
            <>
              <button type="button" data-testid="lightbox-prev"
                onClick={() => setIdx((i) => (i - 1 + imgs.length) % imgs.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button type="button" data-testid="lightbox-next"
                onClick={() => setIdx((i) => (i + 1) % imgs.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center">
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-white font-semibold text-sm">{product.name}</p>
          {imgs.length > 1 && (
            <div className="flex gap-2">
              {imgs.map((im, i) => (
                <button key={i} type="button" onClick={() => setIdx(i)}
                  className={`w-12 h-12 rounded-lg overflow-hidden product-thumb-light border-2 ${i === current ? 'border-[#D9B35A]' : 'border-transparent opacity-60'}`}>
                  <img src={im} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RestockAlertButton = ({ product, zone, subscribedSet, onToggle }) => {
  if (!zone || !getSessionToken()) return null;
  const key = `${product.id}:${zone}`;
  const subscribed = subscribedSet.has(key);
  const toggle = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v2/catalog/restock-alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ product_id: product.id, zone_code: zone }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      onToggle(key, d.subscribed);
      toast.success(d.message);
    } catch {
      toast.error("Impossible d'enregistrer l'alerte");
    }
  };
  return (
    <button type="button" onClick={toggle} data-testid={`restock-alert-${product.sku}`}
      className={`mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
        subscribed
          ? 'text-[#8CC63E] bg-[#8CC63E]/10 border-[#8CC63E]/40'
          : 'text-[#E9CF8E] bg-[#D9B35A]/10 border-[#D9B35A]/30 hover:bg-[#D9B35A]/20'
      }`}>
      <BellRing className="w-3.5 h-3.5" />
      {subscribed ? 'Alerte activée — vous serez prévenu' : 'Me prévenir au retour en stock'}
    </button>
  );
};

const COUNTRY_FLAGS = {
  CUBA: 'cu', HAITI: 'ht', BRESIL: 'br', MAROC: 'ma', FRANCE: 'fr', SENEGAL: 'sn',
  'COTE-DIVOIRE': 'ci', 'AFRIQUE-DU-SUD': 'za', MADAGASCAR: 'mg', MAURICE: 'mu',
};

export const ProductsGrid = ({ products, cart, cartLoading, handleAddToCart, initialSheetProduct = null }) => {
  const restockZone = cart?.zone_code || null;
  const [subscribedSet, setSubscribedSet] = useState(new Set());
  useEffect(() => {
    if (!getSessionToken()) return;
    fetch(`${API_URL}/api/v2/catalog/restock-alerts/mine`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : { alerts: [] }))
      .then((d) => setSubscribedSet(new Set((d.alerts || []).map((a) => `${a.product_id}:${a.zone_code}`))))
      .catch(() => {});
  }, []);
  const toggleSubscribed = (key, subscribed) => {
    setSubscribedSet((prev) => {
      const next = new Set(prev);
      if (subscribed) next.add(key); else next.delete(key);
      return next;
    });
  };
  const [videoProduct, setVideoProduct] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [reviewsProduct, setReviewsProduct] = useState(null);
  const [sheetProduct, setSheetProduct] = useState(null);
  useEffect(() => {
    if (initialSheetProduct) setSheetProduct(initialSheetProduct);
  }, [initialSheetProduct]);
  const [oscopBuyProduct, setOscopBuyProduct] = useState(null);
  const promos = useCatalogPromos();
  const lang = langKey();
  const tr = (p) => (lang !== 'fr' && p.translations?.[lang]) || {};
  return (
  <>
        {/* Products Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {products.map(product => {
            const { discount } = bestPromos(promos, product);
            return (
            <div 
              key={product.id}
              className="glass-panel-soft rounded-[18px] p-4 flex flex-col group"
              data-testid={`product-card-${product.sku}`}
            >
              {/* Product Image gallery (carousel jusqu'à 3 photos) */}
              <div className="aspect-square rounded-xl product-thumb-light mb-4 flex items-center justify-center relative overflow-hidden">
                <ProductImageCarousel product={product} onZoom={(p, index) => setZoom({ product: p, index })} />
                {/* Badge coup de cœur (note >= 4.5) */}
                {product.rating_avg >= 4.5 && (
                  <span
                    data-testid={`product-top-badge-${product.sku}`}
                    className="absolute top-2 left-2 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-semibold text-white shadow-lg"
                    style={{ background: 'linear-gradient(90deg, #C0392B, #E74C3C)' }}
                  >
                    <Heart size={11} fill="currentColor" />
                    {i18n.t('catalog.coup_de_coeur')}
                  </span>
                )}
                {/* Favorite button - positioned top right */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <FavoriteButton 
                    productId={product.id} 
                    productName={product.name}
                    size="sm"
                  />
                </div>
                {/* Spot vidéo badge */}
                {product.video_url && (
                  <button type="button"
                    onClick={() => setVideoProduct(product)}
                    data-testid={`product-video-badge-${product.sku}`}
                    className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-semibold text-black bg-[#D9B35A] hover:bg-[#c9a34a] transition-colors shadow-lg">
                    <Play size={11} fill="currentColor" /> Spot vidéo
                  </button>
                )}
              </div>
              
              {/* Category badge */}
              <Badge variant="outline" className="w-fit mb-2 text-[10px] text-white/60 border-white/20">
                {tData(product.category_name) || tData('Produit')}
              </Badge>
              {/* Bandeau compte à rebours promotion */}
              {product.promo_days_left != null && (
                <div className="w-fit mb-2 px-2 py-1 rounded-md text-[10px] font-bold flex items-center gap-1.5 text-black bg-[#D9B35A] shadow-lg animate-pulse"
                  data-testid={`promo-countdown-${product.sku}`}>
                  <Timer size={11} />
                  Promo — se termine dans {product.promo_days_left} jour{product.promo_days_left > 1 ? 's' : ''}
                </div>
              )}
              {(product.countries || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid={`product-countries-${product.sku}`}>
                  <span className="text-[9px] uppercase tracking-wide text-white/40">Disponible :</span>
                  {product.countries.map((c) => COUNTRY_FLAGS[c] && (
                    <img key={c} src={`https://flagcdn.com/w20/${COUNTRY_FLAGS[c]}.png`} alt={c} title={c}
                      width={18} height={12} className="rounded-[2px] border border-white/10" />
                  ))}
                </div>
              )}
              
              {(product.available_zones || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-1 mb-2" data-testid={`product-zones-${product.sku}`}>
                  <span className="text-[9px] uppercase tracking-wide text-white/40">Zones :</span>
                  {product.available_zones.map((z) => (
                    <span key={z} className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">{z}</span>
                  ))}
                </div>
              )}

              <SaleModelBadge product={product} />
              {product.cb_only_payment && (
                <span data-testid={`cb-only-badge-${product.sku}`}
                  className="w-fit mb-2 px-1.5 py-0.5 rounded text-[9px] font-bold text-emerald-300 bg-emerald-400/10 border border-emerald-400/30">
                  ⚡ Paiement instantané par carte
                </span>
              )}
              {/* Fiche circuit — vendeur juridique / facture / financement */}
              <div className="grid gap-0.5 mb-2 text-[10.5px]" data-testid={`offer-legal-${product.sku}`}>
                <div className="flex items-center gap-1.5 text-white/60">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white overflow-hidden shrink-0">
                    <img
                      src={product.sale_model === 'OSCOP_DIRECT_RESALE' ? '/logos/oscop.webp' : '/logos/kdmarche-pro-gold.webp'}
                      alt={product.sale_model === 'OSCOP_DIRECT_RESALE' ? "O'SCOP — Objectif SCOP Outremer" : 'KDMARCHÉ Pro'}
                      className="no-logo-chip w-4 h-4 object-contain"
                      data-testid={`seller-logo-${product.sku}`} />
                  </span>
                  <span>
                    {i18n.t('catalog.legal_seller')} <b className="text-white/90">
                      {product.sale_model === 'OSCOP_DIRECT_RESALE' ? "O'SCOP — SCIC SAS OBJECTIF SCOP OUTREMER" : (product.seller_name || i18n.t('catalog.partner_default'))}
                    </b>
                  </span>
                </div>
                <div className="text-white/60">
                  {i18n.t('catalog.invoice_by')} <b className="text-white/90">
                    {product.sale_model === 'OSCOP_DIRECT_RESALE' ? "O'SCOP" : (product.seller_name || i18n.t('catalog.the_partner').toLowerCase())}
                  </b>
                </div>
              </div>
              {product.sale_model === 'OSCOP_DIRECT_RESALE' && product.oscop_price_ht_cents > 0 && (
                <Button
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); setOscopBuyProduct(product); }}
                  data-testid={`oscop-buy-btn-${product.sku}`}
                  className="mb-2 w-fit h-7 text-[11px] bg-amber-500/20 hover:bg-amber-500/35 text-amber-200 border border-amber-400/40"
                >
                  Acheter à O'SCOP — {(product.oscop_price_ht_cents / 100).toFixed(2).replace('.', ',')} € HT
                </Button>
              )}

              {/* Product info */}
              <h3 className="font-medium text-white/90 mb-1 line-clamp-2 cursor-pointer hover:text-[#D9B35A] transition-colors"
                onClick={() => setSheetProduct(product)}
                data-testid={`product-sheet-open-${product.sku}`}>{tr(product).name || product.name}</h3>
              {(tr(product).short_description || tr(product).description || product.description) && (
                <p className="text-xs text-white/55 mb-1 line-clamp-2" data-testid={`product-desc-${product.sku}`}>
                  {tr(product).short_description || tr(product).description || product.description}
                </p>
              )}
              <p className="text-xs text-white/50 mb-2">{product.sku} · {product.unit_quantity} {product.unit}</p>

              {/* Avis + partage */}
              <div className="flex items-center justify-between gap-2 mb-2">
                <button type="button" onClick={() => setReviewsProduct(product)}
                  data-testid={`product-reviews-btn-${product.sku}`}
                  className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-[#D9B35A] transition-colors">
                  {product.rating_count > 0 ? (
                    <>
                      <Stars value={product.rating_avg} size={12} />
                      <span className="font-semibold text-[#D9B35A]">{product.rating_avg}</span>
                      <span>({product.rating_count})</span>
                    </>
                  ) : (
                    <>
                      <MessageSquarePlus className="w-3.5 h-3.5" />
                      {i18n.t('catalog.donner_avis')}
                    </>
                  )}
                </button>
                <ProductShareButtons product={product} />
              </div>

              {/* Incoterms + type de livraison badges */}
              {((product.incoterms && Object.keys(product.incoterms).length > 0) || product.incoterm || product.delivery_type) && (
                <div className="flex flex-wrap gap-1 mb-3" data-testid={`product-incoterms-${product.sku}`}>
                  {[...new Set([
                    ...(product.incoterms ? Object.values(product.incoterms).flat() : []),
                    ...(product.incoterm ? [product.incoterm] : []),
                  ])].map((code) => (
                    <span
                      key={code}
                      title={`Incoterm ${code}${product.incoterms ? ` — ${Object.entries(product.incoterms).filter(([, c]) => (c || []).includes(code)).map(([z]) => z).join(', ')}` : ''}`}
                      className="px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wide bg-[#D9B35A]/15 text-[#D9B35A] border border-[#D9B35A]/30"
                    >
                      {code}
                    </span>
                  ))}
                  {product.delivery_type && (
                    <span data-testid={`delivery-type-badge-${product.sku}`}
                      className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-sky-500/15 text-sky-300 border border-sky-500/30">
                      🚚 {i18n.t(`catalog.dt_${DELIVERY_TYPES.indexOf(product.delivery_type)}`, product.delivery_type)}
                    </span>
                  )}
                </div>
              )}
              
              {/* Règlement à Réception Pro / EXW */}
              <div className="flex flex-wrap gap-1 mb-3">
                {product.rar_eligible ? (
                  <span data-testid={`rar-badge-${product.sku}`}
                    className="px-1.5 py-0.5 rounded text-[9px] font-bold text-emerald-300 bg-emerald-400/10 border border-emerald-400/30">
                    {i18n.t('catalog.badge_rar')}
                  </span>
                ) : (
                  <span data-testid={`exw-badge-${product.sku}`}
                    className="px-1.5 py-0.5 rounded text-[9px] font-bold text-sky-300 bg-sky-400/10 border border-sky-400/30">
                    {i18n.t('catalog.badge_exw')}
                  </span>
                )}
              </div>

              {/* Price & Add to cart */}
              <div className="mt-auto flex items-end justify-between">
                <div>
                  {product.price_visible ? (
                    <PromoPriceBlock product={product} discount={discount} />
                  ) : (
                    <div data-testid={`price-locked-${product.sku}`}>
                      <p className="text-lg font-bold text-[#D9B35A] blur-[6px] select-none" aria-hidden="true">
                        {(((product.sku || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 3200) / 100 + 6).toFixed(2).replace('.', ',')} € <span className="text-xs font-normal text-white/50">HT</span>
                      </p>
                      <p className="flex items-center gap-1 text-[10px] text-[#D9B35A]/90 mt-0.5">
                        <Lock className="w-3 h-3" />
                        {i18n.t('catalog.tarif_adherent')}
                      </p>
                    </div>
                  )}
                </div>
                
                <Button
                  size="sm"
                  onClick={() => handleAddToCart(product)}
                  disabled={!product.price_visible || !product.in_stock || cartLoading}
                  className="bg-[#D9B35A] hover:bg-[#c9a34a] text-black"
                  data-testid={`add-to-cart-${product.sku}`}
                >
                  {cartLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Disponibilité réelle par territoire */}
              {!product.in_stock ? (
                <div className="mt-2">
                  <p className="text-xs text-red-400" data-testid={`stock-out-${product.sku}`}>Rupture de stock sur ce territoire</p>
                  <RestockAlertButton product={product} zone={restockZone} subscribedSet={subscribedSet} onToggle={toggleSubscribed} />
                </div>
              ) : product.price_visible && typeof product.stock_quantity === 'number' ? (
                <p className={`text-xs mt-2 flex items-center gap-1.5 ${product.stock_quantity <= 10 ? 'text-amber-400' : 'text-emerald-400'}`}
                  data-testid={`stock-availability-${product.sku}`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${product.stock_quantity <= 10 ? 'bg-amber-400' : 'bg-emerald-400'}`}></span>
                  {product.stock_quantity <= 10
                    ? `Plus que ${product.stock_quantity} unité${product.stock_quantity > 1 ? 's' : ''} disponible${product.stock_quantity > 1 ? 's' : ''} sur ce territoire`
                    : `${product.stock_quantity} unités disponibles sur ce territoire`}
                </p>
              ) : null}
            </div>
            );
          })}
        </div>

        {products.length === 0 && (
          <div className="text-center py-20 text-white/50">
            <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">{i18n.t('catalog.no_products')}</p>
            <p className="text-sm">Essayez de modifier vos filtres</p>
          </div>
        )}
        {videoProduct && <ProductVideoModal product={videoProduct} onClose={() => setVideoProduct(null)} />}
        {oscopBuyProduct && <OscopBuyDialog product={oscopBuyProduct} onClose={() => setOscopBuyProduct(null)} />}
        {reviewsProduct && <ProductReviewsModal product={reviewsProduct} onClose={() => setReviewsProduct(null)} />}
        {sheetProduct && <ProductSheetModal product={sheetProduct} onClose={() => setSheetProduct(null)}
          onAddToCart={handleAddToCart} cartLoading={cartLoading} />}
        {zoom && <ProductLightbox key={`${zoom.product.id}-${zoom.index}`} zoom={zoom} onClose={() => setZoom(null)} />}
  </>
  );
};
